<template>
  <img
    ref="imageRef"
    class="transform-gpu"
    :class="[
      {
        'opacity-0 transition-opacity duration-400 ease-linear':
          loading === 'lazy' && effect === 'fade',
        '!opacity-100': loading === 'lazy' && effect === 'fade' && isVisible
      },
      {
        'scale-75 transition-all duration-700 ease-quart-out [clip-path:circle(0%_at_50%_50%)]':
          loading === 'lazy' && effect === 'mask',
        '!scale-100 [clip-path:circle(50%_at_50%_50%)]':
          loading === 'lazy' && effect === 'mask' && isVisible
      }
    ]"
    :src="src"
    :srcset="srcset"
    :sizes="sizes"
    :loading="loading"
    :width="width"
    :height="height"
    :alt="alt"
    @load="onLoad($event)"
    @error="onError($event)"
  />
</template>

<script setup lang="ts">
import type { UseIntersectionObserverOptions } from '@vueuse/core'

export interface Image {
  preload?: boolean
  src: string | undefined
  srcset?: string | undefined
  width: number
  height: number
  alt: string
  sizes?: string
  loading?: 'eager' | 'lazy' | undefined
  effect?: 'fade' | 'mask' | 'none'
  observerOptions?: UseIntersectionObserverOptions
}

const props = withDefaults(defineProps<Image>(), {
  effect: 'fade',
  loading: undefined,
  srcset: undefined,
  sizes: 'auto',
  observerOptions: () => ({
    threshold: 0
  })
})

const imageRef = ref<HTMLElement | null>(null)
const isLoaded = ref(false)
const isVisible = ref(false)

defineExpose({
  imageRef
})

const emit = defineEmits(['load'])

function onLoad(e: Event) {
  isLoaded.value = true
  emit('load', e)
}

function onError(e: Event) {
  console.error(e)
}

const { stop } = useIntersectionObserver(
  imageRef,
  ([{ isIntersecting }], _observerElement) => {
    if (isIntersecting) {
      isVisible.value = true
    }
  },
  props.observerOptions
)

onUnmounted(() => {
  stop()
})

if (props.preload) {
  useHead({
    link: [
      {
        rel: 'preload',
        fetchpriority: 'high',
        as: 'image',
        imagesrcset: props.srcset ? props.srcset : undefined,
        imagesizes: props.sizes ? props.sizes : undefined,
        href: !props.srcset ? props.src : undefined
      }
    ]
  })
}
</script>
